import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { capture } from './tracking/posthog';
import './Homepage.css';

/* ─── Scroll-Reveal Hook ────────────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('hp-visible'); observer.disconnect(); } },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

const APP_STORE_URL = 'https://apps.apple.com/de/app/cvflow/id6778375720';
const isMobile = () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

function starteOnboarding(navigate, label = 'Jetzt kostenlos testen', position = 'unknown') {
  capture('cta_clicked', { cta_label: label, position });
  if (isMobile()) { window.location.href = APP_STORE_URL; return; }
  localStorage.removeItem('onboarding_done');
  navigate('/onboarding');
}

function handleLogin(navigate) {
  if (isMobile()) { window.location.href = APP_STORE_URL; return; }
  navigate('/login');
}

function scrollToSection(event, id) {
  event.preventDefault();
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
}

/* ─── NavBar ────────────────────────────────────────────────────── */
function NavBar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);
  const close = () => setMenuOpen(false);
  return (
    <>
      <nav className={`hp-nav${scrolled ? ' hp-nav--scrolled' : ''}`} role="navigation" aria-label="Hauptnavigation">
        <div className="hp-nav__inner">
          <a href="/" className="hp-nav__logo">
            <img src="/logo.png" alt="CVFlow" className="hp-nav__logo-mark" />
          </a>
          <div className="hp-nav__left">
            <a href="#autopilot" className="hp-nav__link" onClick={(e) => scrollToSection(e, 'autopilot')}>Features</a>
            <a href="#preise" className="hp-nav__link" onClick={(e) => scrollToSection(e, 'preise')}>Preise</a>
            <a href="#datenschutz" className="hp-nav__link" onClick={(e) => scrollToSection(e, 'datenschutz')}>Datenschutz</a>
            <a href="#faq" className="hp-nav__link" onClick={(e) => scrollToSection(e, 'faq')}>FAQ</a>
            <a href="/ratgeber/" className="hp-nav__link">Ratgeber</a>
          </div>
          <div className="hp-nav__links">
            <button className="hp-nav__login" onClick={() => { capture('cta_clicked', { cta_label: 'Login', position: 'nav' }); handleLogin(navigate); }} type="button">
              Login
            </button>
            <button className="hp-btn hp-btn--amber hp-btn--sm" onClick={() => starteOnboarding(navigate, 'Jetzt kostenlos testen', 'nav')} type="button">
              Jetzt kostenlos testen
            </button>
          </div>

          {/* Hamburger — only visible on mobile */}
          <button className="hp-nav__burger" onClick={() => setMenuOpen(o => !o)} aria-label="Menü öffnen" aria-expanded={menuOpen} type="button">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Mobile drawer — rendered outside <nav> so backdrop-filter doesn't trap fixed positioning */}
      {menuOpen && <div className="hp-nav__overlay" onClick={close} />}
      <div className={`hp-nav__drawer${menuOpen ? ' hp-nav__drawer--open' : ''}`}>
        <a href="#autopilot" className="hp-nav__drawer-link" onClick={(e) => { scrollToSection(e, 'autopilot'); close(); }}>Features</a>
        <a href="#preise" className="hp-nav__drawer-link" onClick={(e) => { scrollToSection(e, 'preise'); close(); }}>Preise</a>
        <a href="#datenschutz" className="hp-nav__drawer-link" onClick={(e) => { scrollToSection(e, 'datenschutz'); close(); }}>Datenschutz</a>
        <a href="#faq" className="hp-nav__drawer-link" onClick={(e) => { scrollToSection(e, 'faq'); close(); }}>FAQ</a>
        <a href="/ratgeber/" className="hp-nav__drawer-link">Ratgeber</a>
        <div className="hp-nav__drawer-divider" />
        <button className="hp-nav__login hp-nav__drawer-link" onClick={() => { handleLogin(navigate); close(); }} type="button">Login</button>
        <button className="hp-btn hp-btn--amber hp-nav__drawer-cta" onClick={() => { starteOnboarding(navigate, 'Jetzt kostenlos testen', 'nav'); close(); }} type="button">Jetzt kostenlos testen</button>
      </div>
    </>
  );
}

/* ─── Hero ──────────────────────────────────────────────────────── */
function Hero() {
  const ref = useReveal();
  const laptopRef = useReveal();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');

  return (
    <section className="hp-hero" aria-labelledby="hero-h1">
      <div className="hp-hero__bg-noise" aria-hidden="true" />

      <div className="hp-hero__inner">
        <div className="hp-hero__content">
        <h1 id="hero-h1" className="hp-hero__eyebrow">
          KI-Bewerbungsassistent für Lebenslauf &amp; Anschreiben
        </h1>
        <p className="hp-hero__h1">
          Bewerben kann auch<br />
          <span className="hp-hero__h1-accent">einfach </span><span style={{color:'var(--hp-white)'}}>gehen.</span>
        </p>

        <p className="hp-hero__sub">
          Füge eine Job-URL oder Stellenanzeige ein. CVFlow führt dich Schritt für
          Schritt zu einem passenden Lebenslauf und Anschreiben, die du prüfen,
          anpassen und exportieren kannst.
        </p>

        <div className="hp-hero__input-row">
          <div className="hp-hero__input-wrap">
            <span className="hp-hero__input-icon" aria-hidden="true">🔗</span>
            <input
              type="url"
              className="hp-hero__input"
              placeholder="Job-Link oder Stellenanzeige einfügen"
              value={url}
              onChange={e => setUrl(e.target.value)}
              aria-label="Stellenanzeigen-URL eingeben"
            />
          </div>
          <button
            className="hp-btn hp-btn--amber hp-btn--hero"
            onClick={() => starteOnboarding(navigate, 'Bewerbung starten', 'hero')}
            type="button"
          >
            Bewerbung starten
          </button>
        </div>

        <div className="hp-hero__trust" aria-label="Vertrauenssignale">
          <span className="hp-hero__trust-item"><span className="hp-hero__trust-check" aria-hidden="true">✓</span> Klarer Ablauf</span>
          <span className="hp-hero__trust-sep" aria-hidden="true">·</span>
          <span className="hp-hero__trust-item"><span className="hp-hero__trust-check" aria-hidden="true">✓</span> Inhalte selbst prüfen</span>
          <span className="hp-hero__trust-sep" aria-hidden="true">·</span>
          <span className="hp-hero__trust-item"><span className="hp-hero__trust-check" aria-hidden="true">✓</span> Direkt exportieren</span>
        </div>
        </div>

        {/* ── Phone Mockup ── */}
        <div className="hp-hero__phone-wrap" aria-hidden="true">
          <img src={`${process.env.PUBLIC_URL}/phone_mockup.png`} alt="" className="hp-hero__phone-img" />
        </div>
      </div>
    </section>
  );
}

/* ─── Pipeline SVG Icons ────────────────────────────────────────── */
function LinkIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip-pl-link)">
        <path d="M12.5 14.75H10C9.59 14.75 9.25 14.41 9.25 14C9.25 13.59 9.59 13.25 10 13.25H12.5C15.12 13.25 17.25 11.12 17.25 8.5C17.25 5.88 15.12 3.75 12.5 3.75H7.5C4.88 3.75 2.75 5.88 2.75 8.5C2.75 9.6 3.14 10.67 3.84 11.52C4.1 11.84 4.06 12.31 3.74 12.58C3.42 12.84 2.95 12.8 2.68 12.48C1.76 11.36 1.25 9.95 1.25 8.5C1.25 5.05 4.05 2.25 7.5 2.25H12.5C15.95 2.25 18.75 5.05 18.75 8.5C18.75 11.95 15.95 14.75 12.5 14.75Z" fill="currentColor"/>
        <path d="M16.5 21.75H11.5C8.05 21.75 5.25 18.95 5.25 15.5C5.25 12.05 8.05 9.25 11.5 9.25H14C14.41 9.25 14.75 9.59 14.75 10C14.75 10.41 14.41 10.75 14 10.75H11.5C8.88 10.75 6.75 12.88 6.75 15.5C6.75 18.12 8.88 20.25 11.5 20.25H16.5C19.12 20.25 21.25 18.12 21.25 15.5C21.25 14.4 20.86 13.33 20.16 12.48C19.9 12.16 19.94 11.69 20.26 11.42C20.58 11.15 21.05 11.2 21.32 11.52C22.25 12.64 22.76 14.05 22.76 15.5C22.75 18.95 19.95 21.75 16.5 21.75Z" fill="currentColor"/>
      </g>
      <defs><clipPath id="clip-pl-link"><rect width="24" height="24" fill="white"/></clipPath></defs>
    </svg>
  );
}

function DocIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip-pl-doc)">
        <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15.75 9H8.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15.75 15H8.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </g>
      <defs><clipPath id="clip-pl-doc"><rect width="24" height="24" fill="white"/></clipPath></defs>
    </svg>
  );
}

/* ─── Autopilot (Der Workflow) ──────────────────────────────────── */
function Autopilot() {
  const headRef  = useReveal();
  const subRef   = useReveal();
  const pipeRef  = useReveal();
  const brakeRef = useReveal();

  const agents = [
    { key: 'analyst', letter: 'A', name: 'Job Analyst',  desc: 'Erkennt Muss-Kriterien und Keywords' },
    { key: 'expert',  letter: 'E', name: 'Resume Expert', desc: 'Passt Lebenslauf und Anschreiben an' },
    { key: 'checker', letter: '✓', name: 'Fact-Checker', desc: 'Vergleicht jedes Wort mit deinem Profil', amber: true },
  ];

  return (
    <section className="hp-autopilot" id="autopilot" aria-labelledby="autopilot-h2">
      <div className="hp-section__inner">

        <h2 id="autopilot-h2" className="hp-section__h2 hp-autopilot__h2 hp-reveal" ref={headRef}>
          Schluss mit Bewerbungschaos.
        </h2>
        <p className="hp-autopilot__sub hp-reveal" ref={subRef}>
          Statt zwischen Stellenanzeige, Lebenslauf, Anschreiben und Formatierung hin und her zu springen,
          arbeitest du in einem klaren Ablauf. CVFlow zeigt dir, was zur Stelle passt, was noch fehlt
          und was du vor dem Export prüfen solltest.
        </p>

        {/* ── Ohne CVFlow Panel ── */}
        <div className="hp-chaos-panel">
          <span className="hp-chaos-panel__label">Ohne CVFlow</span>
          <div className="hp-chaos-panel__steps">
            {[
              'Stellenanzeige lesen',
              'Keywords suchen',
              'Lebenslauf manuell\numstellen',
              'Anschreiben neu\nformulieren',
              'Formatierung prüfen',
              'Unsicher sein,\nob alles passt',
            ].map((step, i, arr) => (
              <React.Fragment key={step}>
                <div className="hp-chaos-panel__card">
                  {step.split('\n').map((line, j) => (
                    <span key={j}>{line}{j < step.split('\n').length - 1 && <br />}</span>
                  ))}
                </div>
                {i < arr.length - 1 && <span className="hp-chaos-panel__arrow" aria-hidden="true">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── Mit CVFlow Panel ── */}
        <div className="hp-flow-panel">
          <span className="hp-flow-panel__label">Mit CVFlow</span>
          <div className="hp-flow-panel__steps">
            {[
              'Job-Link einfügen',
              'Lebenslauf und Anschreiben\nvorbereiten lassen',
              'Inhalte in Ruhe prüfen',
              'Anpassungen vornehmen',
              'Exportieren,\nwenn alles passt',
            ].map((step, i, arr) => (
              <React.Fragment key={step}>
                <div className="hp-flow-panel__card">
                  {step.split('\n').map((line, j) => (
                    <span key={j}>{line}{j < step.split('\n').length - 1 && <br />}</span>
                  ))}
                </div>
                {i < arr.length - 1 && <span className="hp-flow-panel__arrow" aria-hidden="true">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Dashboard icons ───────────────────────────────────────────── */
function CalIcon({ size = 13, color = '#9e9080' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 3, flexShrink: 0 }}>
      <g clipPath="url(#cal-clip)">
        <path d="M8 2V5" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 2V5" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.5 9.08984H20.5" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.6947 13.6992H15.7037" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.6947 16.6992H15.7037" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.9955 13.6992H12.0045" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.9955 16.6992H12.0045" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.29431 13.6992H8.30329" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.29431 16.6992H8.30329" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <defs>
        <clipPath id="cal-clip"><rect width="24" height="24" fill="white" /></clipPath>
      </defs>
    </svg>
  );
}

function ChevronIcon({ size = 16, color = '#b0a898' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.9201 8.9502L13.4001 15.4702C12.6301 16.2402 11.3701 16.2402 10.6001 15.4702L4.08008 8.9502"
        stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── Dashboard (Bewerbungs-Zentrale) ───────────────────────────── */
function Dashboard() {
  const textRef = useReveal();
  const mockRef = useReveal();
  const navigate = useNavigate();
  const stellenCards = [
    {
      company: 'Deloitte',
      badge: 'Kennenlerngespräch',
      badgeCls: 'orange',
      fields: [
        { label: 'ANSPRECHPARTNER', value: 'Herr Mustermann' },
        { label: 'ABGESCHICKT', value: '04/15/2026', cal: true },
        { label: 'INTERVIEW', value: '04/23/2026', cal: true },
        { label: 'GEHALTSVORSTELLUNG', value: '67.000\u202f€' },
        { label: 'ERSTELLT', value: '15.04.2026', muted: true },
      ],
    },
    {
      company: 'Dataport',
      badge: 'Zusage',
      badgeCls: 'green',
      fields: [
        { label: 'ANSPRECHPARTNER', value: 'Frau Musterfrau' },
        { label: 'ABGESCHICKT', value: '04/15/2026', cal: true },
        { label: 'GEHALTSVORSTELLUNG', value: '75.000\u202f€' },
        { label: 'ERSTELLT', value: '15.04.2026', muted: true },
      ],
    },
    {
      company: 'Google',
      badge: 'Zusage',
      badgeCls: 'green',
      fields: [
        { label: 'ANSPRECHPARTNER', value: '—' },
        { label: 'ABGESCHICKT', value: '04/15/2026', cal: true },
        { label: 'GEHALTSVORSTELLUNG', value: '85.000\u202f€' },
        { label: 'ERSTELLT', value: '15.04.2026', muted: true },
      ],
    },
  ];
  return (
    <section className="hp-dashboard" id="dashboard" aria-labelledby="dashboard-h2">
      <div className="hp-section__inner hp-dashboard__layout">
        <div className="hp-dashboard__text hp-reveal" ref={textRef}>
          <h2 id="dashboard-h2" className="hp-section__h2 hp-section__h2--light">
            Jede Bewerbung<br />an einem Ort.
          </h2>
          <p className="hp-section__lead hp-section__lead--light">
            Aus einer Stellenanzeige werden Unterlagen, Status, Termine und Entwürfe.
            Du siehst sofort, was offen ist, welche Bewerbung verschickt wurde und
            welcher nächste Schritt ansteht.
          </p>
          <ul className="hp-dashboard__features" aria-label="Dashboard-Features">
            {[
              'Status verfolgen: Entwurf, abgeschickt, eingeladen, Zusage',
              'Interview-Termine automatisch im Kalender festhalten',
              'Kalender-Sync mit Google, Outlook & Co.',
              'Übersicht aller Stellen, Dokumente und Entwürfe',
            ].map((f, i) => (
              <li key={i} className="hp-dashboard__feature">
                <span className="hp-dashboard__feature-check" aria-hidden="true">✓</span>
                {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="hp-dashboard__mock hp-reveal" ref={mockRef} style={{ transitionDelay: '160ms' }} aria-hidden="true">
          <div className="hp-mock-stellenliste">
            {stellenCards.map((card, i) => {
              const mainFields = card.fields.filter(f => !f.muted);
              const erstelltField = card.fields.find(f => f.muted);
              return (
                <div key={i} className="hp-mock-stelle">
                  <div className="hp-mock-stelle__header">
                    <span className="hp-mock-stelle__name">{card.company}</span>
                    <span className={`hp-mock-stelle__badge hp-mock-stelle__badge--${card.badgeCls}`}>{card.badge}</span>
                    <span className="hp-mock-stelle__chevron"><ChevronIcon size={14} color="#b0a898" /></span>
                  </div>
                  <div className="hp-mock-stelle__meta">
                    <div className="hp-mock-stelle__meta-main">
                      {mainFields.map((f, j) => (
                        <div key={j} className="hp-mock-stelle__field">
                          <div className="hp-mock-stelle__field-label">{f.label}</div>
                          <div className="hp-mock-stelle__field-value">
                            {f.value}{f.cal && <CalIcon size={12} color="#9e9080" />}
                          </div>
                        </div>
                      ))}
                    </div>
                    {erstelltField && (
                      <div className="hp-mock-stelle__field hp-mock-stelle__field--muted">
                        <div className="hp-mock-stelle__field-label">{erstelltField.label}</div>
                        <div className="hp-mock-stelle__field-value">{erstelltField.value}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="hp-dashboard__btn-row">
          <button className="hp-mock-stellenliste__btn" onClick={() => starteOnboarding(navigate, 'Jetzt Übersicht schaffen', 'dashboard')} type="button">
            Jetzt Übersicht schaffen
          </button>
        </div>
      </div>
    </section>
  );
}

/* ─── Editor ────────────────────────────────────────────────────── */
function Editor() {
  const textRef = useReveal();
  const docRef = useReveal();
  const [fmt, setFmt] = React.useState({ bold: false, italic: false });
  const bold = fmt.bold;
  const italic = fmt.italic;
  function toggleBold() { setFmt(f => ({ ...f, bold: !f.bold })); }
  function toggleItalic() { setFmt(f => ({ ...f, italic: !f.italic })); }
  const [jobs, setJobs] = React.useState([
    { id: 1, role: 'Lead Developer', company: 'Zalando SE', ai: 'Leitete die Migration eines Monolithen auf Microservices mit 3 Cross-functional Teams — Release-Zyklen von monatlich auf wöchentlich verkürzt.' },
    { id: 2, role: 'Frontend Developer', company: 'AboutYou', ai: 'Entwickelte das komponentenbasierte Design-System in React, das konzernweit von über 20 Teams genutzt wird.' },
  ]);
  const [aiPhase, setAiPhase] = React.useState(null); // null | 'loading' | 'done'
  function onKiClick() {
    if (aiPhase) return;
    setAiPhase('loading');
    setTimeout(() => setAiPhase('done'), 1400);
  }
  const [dragging, setDragging] = React.useState(null);
  const [over, setOver] = React.useState(null);
  const [ghostPos, setGhostPos] = React.useState(null);
  const jobRefs = React.useRef([]);
  const mockRef = React.useRef(null);
  const fromRef = React.useRef(null);
  const overRef = React.useRef(null);
  const dragOffset = React.useRef({ x: 0, y: 0 });
  const ghostWidth = React.useRef(0);

  const ease = 'transform 0.2s cubic-bezier(0.25,0.46,0.45,0.94)';
  function jobStyle(i) {
    if (dragging === null || over === null || dragging === over) return {};
    if (i === dragging) return { opacity: 0, userSelect: 'none' };
    const h = (jobRefs.current[dragging]?.offsetHeight ?? 64) + 12;
    if (dragging < over && i > dragging && i <= over) return { transform: `translateY(-${h}px)`, transition: ease };
    if (dragging > over && i < dragging && i >= over) return { transform: `translateY(${h}px)`, transition: ease };
    return {};
  }

  function calcGhost(clientX, clientY) {
    const mr = mockRef.current?.getBoundingClientRect();
    if (!mr) return null;
    return { x: clientX - dragOffset.current.x - mr.left, y: clientY - dragOffset.current.y - mr.top };
  }

  function startDrag(e, i) {
    e.preventDefault();
    const itemRect = jobRefs.current[i].getBoundingClientRect();
    dragOffset.current = { x: e.clientX - itemRect.left, y: e.clientY - itemRect.top };
    ghostWidth.current = jobRefs.current[i].offsetWidth;
    fromRef.current = i;
    overRef.current = i;
    setDragging(i);
    setOver(i);
    setGhostPos(calcGhost(e.clientX, e.clientY));

    function onMove(ev) {
      setGhostPos(calcGhost(ev.clientX, ev.clientY));
      let found = overRef.current;
      jobRefs.current.forEach((el, j) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (ev.clientY >= r.top && ev.clientY < r.bottom) found = j;
      });
      if (found !== overRef.current) { overRef.current = found; setOver(found); }
    }

    function onUp() {
      const from = fromRef.current;
      const to = overRef.current;
      fromRef.current = null;
      overRef.current = null;
      setDragging(null);
      setOver(null);
      setGhostPos(null);
      if (from !== null && to !== null && from !== to) {
        setJobs(prev => {
          const next = [...prev];
          const [moved] = next.splice(from, 1);
          next.splice(to, 0, moved);
          return next;
        });
      }
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    }

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }

  return (
    <section className="hp-editor" id="editor" aria-labelledby="editor-h2">
      <div className="hp-section__inner hp-editor__layout">
        <div className="hp-editor__mock hp-reveal" ref={docRef}>
          <div className="hp-mock-cv" ref={mockRef}>
            <div className="hp-mock-cv__header">
              <div className="hp-mock-cv__name">Max Mustermann</div>
              <div className="hp-mock-cv__role">Senior Software Engineer</div>
              <div className="hp-mock-cv__contact">berlin@mail.de · +49 170 000 0000 · LinkedIn</div>
            </div>
            <div className="hp-mock-cv__section">
              <div className="hp-mock-cv__section-head">Berufserfahrung</div>
              {jobs.map((job, i) => (
                <div
                  key={job.id}
                  className="hp-mock-cv__job"
                  ref={el => jobRefs.current[i] = el}
                  style={jobStyle(i)}
                >
                  <span className="hp-mock-cv__drag-handle" onPointerDown={e => startDrag(e, i)}>⠿</span>
                  <div className="hp-mock-cv__job-body">
                    <div className="hp-mock-cv__job-title">
                      {job.role} — {i === 0
                        ? <span className="hp-mock-cv__sel" style={{ fontWeight: bold ? 800 : undefined, fontStyle: italic ? 'italic' : undefined }}>{job.company}</span>
                        : job.company}
                      {i === 0 && <span className="hp-mock-cv__cursor">|</span>}
                    </div>
                    {aiPhase === 'done'
                      ? <div className="hp-mock-cv__ai-text">{job.ai}</div>
                      : <>
                          <div className={`hp-mock-cv__line${aiPhase === 'loading' ? ' hp-mock-cv__line--shimmer' : ''}`} />
                          <div className={`hp-mock-cv__line hp-mock-cv__line--short${aiPhase === 'loading' ? ' hp-mock-cv__line--shimmer' : ''}`} />
                        </>}
                  </div>
                </div>
              ))}
            </div>
            <div className="hp-mock-cv__section">
              <div className="hp-mock-cv__section-head">Skills</div>
              <div className="hp-mock-cv__chips">
                <span className="hp-mock-cv__chip">React</span>
                <span className="hp-mock-cv__chip">Python</span>
                <span className="hp-mock-cv__chip">AWS</span>
                <span className="hp-mock-cv__chip hp-mock-cv__chip--drag">⠿ TypeScript</span>
              </div>
            </div>
            <div className="hp-mock-cv__toolbar">
              <span className={`hp-mock-cv__tb-btn${bold ? ' hp-mock-cv__tb-btn--active' : ''}`} onPointerDown={e => { e.stopPropagation(); toggleBold(); }}>B</span>
              <span className={`hp-mock-cv__tb-btn${italic ? ' hp-mock-cv__tb-btn--active' : ''}`} onPointerDown={e => { e.stopPropagation(); toggleItalic(); }}>I</span>
              <span className="hp-mock-cv__tb-btn hp-mock-cv__tb-btn--ai" onPointerDown={e => { e.stopPropagation(); onKiClick(); }}>KI ✦</span>
            </div>
            {dragging !== null && ghostPos && (() => {
              const job = jobs[dragging];
              return (
                <div
                  className="hp-mock-cv__job hp-mock-cv__job--ghost"
                  style={{ left: ghostPos.x, top: ghostPos.y, width: ghostWidth.current }}
                >
                  <span className="hp-mock-cv__drag-handle">⠿</span>
                  <div className="hp-mock-cv__job-body">
                    <div className="hp-mock-cv__job-title">
                      {job.role} — {dragging === 0
                        ? <span className="hp-mock-cv__sel" style={{ fontWeight: bold ? 800 : undefined, fontStyle: italic ? 'italic' : undefined }}>{job.company}</span>
                        : job.company}
                      {dragging === 0 && <span className="hp-mock-cv__cursor">|</span>}
                    </div>
                    {aiPhase === 'done'
                      ? <div className="hp-mock-cv__ai-text">{jobs[dragging]?.ai}</div>
                      : <><div className="hp-mock-cv__line" /><div className="hp-mock-cv__line hp-mock-cv__line--short" /></>}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        <div className="hp-editor__text hp-reveal" ref={textRef} style={{ transitionDelay: '120ms' }}>
          <h2 id="editor-h2" className="hp-section__h2">
            Lebenslauf bearbeiten ohne Formatierungsstress.
          </h2>
          <p className="hp-section__lead">
            Passe Lebenslauf und Anschreiben direkt in der Vorschau an. Texte,
            Abschnitte und Reihenfolge bleiben flexibel, während das Layout sauber bleibt.
          </p>
          <ul className="hp-editor__features">
            {[
              ['Direkt im Dokument bearbeiten', 'Klick auf Text, ändern, fertig.'],
              ['Abschnitte neu ordnen', 'Erfahrungen, Skills und Inhalte einfach verschieben.'],
              ['Saubere Vorschau', 'Du siehst jederzeit, wie deine Bewerbung als PDF wirkt.'],
            ].map(([title, desc], i) => (
              <li key={i} className="hp-editor__feature-item">
                <div className="hp-editor__feature-title">{title}</div>
                <div className="hp-editor__feature-desc">{desc}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ─── Pricing icons ─────────────────────────────────────────────── */
function StarIcon({ size = 14, color = '#1a1a1a' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4, flexShrink: 0 }}>
      <g clipPath="url(#star-clip)">
        <path d="M13.7299 3.51063L15.4899 7.03063C15.7299 7.52063 16.3699 7.99063 16.9099 8.08063L20.0999 8.61062C22.1399 8.95062 22.6199 10.4306 21.1499 11.8906L18.6699 14.3706C18.2499 14.7906 18.0199 15.6006 18.1499 16.1806L18.8599 19.2506C19.4199 21.6806 18.1299 22.6206 15.9799 21.3506L12.9899 19.5806C12.4499 19.2606 11.5599 19.2606 11.0099 19.5806L8.01991 21.3506C5.87991 22.6206 4.57991 21.6706 5.13991 19.2506L5.84991 16.1806C5.97991 15.6006 5.74991 14.7906 5.32991 14.3706L2.84991 11.8906C1.38991 10.4306 1.85991 8.95062 3.89991 8.61062L7.08991 8.08063C7.61991 7.99063 8.25991 7.52063 8.49991 7.03063L10.2599 3.51063C11.2199 1.60063 12.7799 1.60063 13.7299 3.51063Z"
          stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <defs><clipPath id="star-clip"><rect width="24" height="24" fill="white" /></clipPath></defs>
    </svg>
  );
}

function LockIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'inline', verticalAlign: 'middle', marginRight: 5, flexShrink: 0 }}>
      <g clipPath="url(#lock-clip)">
        <path d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.9965 16H16.0054" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.9955 16H12.0045" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7.99451 16H8.00349" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <defs><clipPath id="lock-clip"><rect width="24" height="24" fill="white" /></clipPath></defs>
    </svg>
  );
}

/* ─── Pricing ───────────────────────────────────────────────────── */
const PAKETE = [
  {
    tag: 'Gratis',
    tagStar: false,
    name: 'CVFlow Free',
    text: 'Zum Reinschnuppern',
    preis: 'Kostenlos',
    gratis: true,
    periode: null,
    features: [
      'Bis zu 3 Bewerbungen verwalten',
      'Analytics-Dashboard',
      'Job-Fit Analyse',
    ],
    cta: 'Kostenlos starten',
    star: false,
  },
  {
    tag: 'Flexibel',
    tagStar: false,
    name: 'Pro Monatlich',
    text: 'Für aktive Bewerbungsphasen',
    preis: '17,99',
    gratis: false,
    periode: '/ Monat',
    trial: true,
    features: [
      'Bewerbungen passend zur Stelle vorbereiten',
      'Lebenslauf und Anschreiben bearbeiten',
      'Job-Fit Analyse',
    ],
    cta: 'Jetzt starten →',
    star: false,
  },
  {
    tag: 'Beliebt',
    tagStar: true,
    name: 'Pro Quartal',
    text: 'Für mehrere Bewerbungen mit Ruhe',
    preis: '29,99',
    gratis: false,
    periode: '/ Quartal',
    trial: true,
    features: [
      'Alles aus Pro',
      'Ideal für längere Bewerbungsphasen',
      'Ca. 10,00 € pro Monat',
      '44 % günstiger als monatlich',
    ],
    cta: 'Jetzt starten →',
    star: true,
  },
  {
    tag: 'Bester Preis',
    tagStar: false,
    name: 'Pro Jahr',
    text: 'Für langfristige Jobsuche und Wechselbereitschaft',
    preis: '99,99',
    gratis: false,
    periode: '/ Jahr',
    trial: true,
    features: [
      'Alles aus Pro',
      'Bewerbungen jederzeit vorbereiten',
      'Ca. 8,33 € pro Monat',
      'Spare über 115 € jährlich',
    ],
    cta: 'Jetzt starten →',
    star: false,
  },
];

function Preise() {
  const headRef = useReveal();
  const navigate = useNavigate();

  return (
    <section className="hp-preise" id="preise" aria-labelledby="preise-h2">
      <div className="hp-section__inner">
        <h2 id="preise-h2" className="hp-preise__h2">
          Ein Abo. Alle Bewerbungen im Griff.
        </h2>
        <p className="hp-preise__lead">
          Erstelle, prüfe und verwalte deine Bewerbungen an einem Ort. Keine versteckten Gebühren, keine Einzelkäufe pro Bewerbung.
        </p>

        <div className="hp-preise__grid">
          {PAKETE.map((p, i) => (
            <article
              key={`${p.name}-${p.periode || p.tag}`}
              className={`hp-preise__card${p.star ? ' hp-preise__card--star' : ''}`}
              style={{ animationDelay: `${i * 80}ms` }}
            >
              {p.trial && <div className="hp-preise__trial">1 Woche kostenlos testen</div>}
              <div className={`hp-preise__badge${p.tagStar ? ' hp-preise__badge--star' : ' hp-preise__badge--default'}`}>
                {p.tagStar && <StarIcon size={14} color="var(--hp-teal-dark)" />}
                {p.tag}
              </div>
              <div className="hp-preise__name">{p.name}</div>
              {p.text && <div className="hp-preise__text">{p.text}</div>}
              <div className="hp-preise__price-row">
                {!p.gratis && <span className="hp-preise__currency">€</span>}
                <span className={`hp-preise__amount${p.gratis ? ' hp-preise__amount--gratis' : ''}`}>{p.preis}</span>
                {p.periode && <span className="hp-preise__periode">{p.periode}</span>}
              </div>
              {p.sub && <div className="hp-preise__quota">{p.sub}</div>}
              <div className="hp-preise__features">
                {p.features.map((f, idx) => (
                  <div key={idx}>
                    <div className="hp-preise__feature-item">{f}</div>
                    {idx < p.features.length - 1 && <div className="hp-preise__feature-divider" />}
                  </div>
                ))}
              </div>
              <button
                className={`hp-preise__cta${p.star ? ' hp-preise__cta--star' : p.gratis ? ' hp-preise__cta--gratis' : ' hp-preise__cta--default'}`}
                onClick={() => starteOnboarding(navigate, p.cta, 'pricing')}
                type="button"
              >
                {p.cta}
              </button>
            </article>
          ))}
        </div>

        <p className="hp-preise__disclaimer">
          <LockIcon size={16} color="currentColor" />
          Sichere Zahlung via Stripe · SSL-verschlüsselt
        </p>
        <p className="hp-preise__ownership">
          <strong>Einmal generiert, dauerhaft deins.</strong>{' '}
          Alle während deines Abos erstellten Unterlagen bleiben auch nach der Kündigung in deinem Account zugänglich.
        </p>
      </div>
    </section>
  );
}

/* ─── Datenschutz ───────────────────────────────────────────────── */
function Datenschutz() {
  return (
    <section className="hp-privacy" id="datenschutz">
      <div className="hp-section__inner hp-privacy__inner">
        <div className="hp-privacy__head">
          <h2 className="hp-privacy__h2">Deine Daten.<br />Deine Kontrolle.</h2>
          <p className="hp-privacy__lead">Bewerbungen enthalten sensible Informationen. Wir behandeln sie entsprechend.</p>
        </div>
        <div className="hp-privacy__grid">
          <div className="hp-privacy__card">
            <div className="hp-privacy__icon">🔒</div>
            <div className="hp-privacy__card-title">Persönliches bleibt persönlich.</div>
            <p className="hp-privacy__card-text">CVFlow trennt Kontaktdaten konsequent von der Texterstellung. Für deine Bewerbung zählen Erfahrung, Skills und die Anforderungen der Stelle — nicht mehr Daten als nötig.</p>
          </div>
          <div className="hp-privacy__card">
            <div className="hp-privacy__icon">🇪🇺</div>
            <div className="hp-privacy__card-title">Datenschutz, der mitgedacht ist.</div>
            <p className="hp-privacy__card-text">Deine Bewerbungsdaten werden nicht zum Training von KI-Modellen verwendet. CVFlow gibt keine Bewerbungsinhalte an Werbetreibende weiter — Analyse- und Marketing-Dienste nutzen wir nur mit deiner Einwilligung und erfassen dabei keine Lebenslauf- oder Anschreibeninhalte.</p>
          </div>
          <div className="hp-privacy__card">
            <div className="hp-privacy__icon">🗑️</div>
            <div className="hp-privacy__card-title">Du hast die Kontrolle.</div>
            <p className="hp-privacy__card-text">Account und Bewerbungsunterlagen kannst du jederzeit selbst löschen. Deine Daten bleiben in deiner Hand.</p>
          </div>
        </div>
        <p className="hp-privacy__footer">
          Mehr Informationen dazu, wie wir mit deinen Daten umgehen, findest du in unserer{' '}
          <Link to="/datenschutz" className="hp-privacy__footer-link">Datenschutzerklärung</Link>.
        </p>
      </div>
    </section>
  );
}

/* ─── Trust & Final CTA ─────────────────────────────────────────── */
function Trust() {
  const headRef = useReveal();
  return (
    <section className="hp-trust" aria-labelledby="trust-h2">
      <div className="hp-section__inner">
        <div className="hp-trust__text hp-reveal" ref={headRef}>
          <h2 id="trust-h2" className="hp-trust__h2">
            Aus der Stellenanzeige wird ein<br />
            <span className="hp-trust__h2-accent">passender Lebenslauf.</span>
          </h2>
          <p className="hp-trust__sub">
            CVFlow erkennt, welche Anforderungen wichtig sind, gleicht sie mit deiner Erfahrung ab und zeigt dir, welche
            Formulierungen du übernehmen, prüfen oder ergänzen solltest
          </p>
        </div>
        <div className="hp-trust__cards">
          <div className="hp-trust__job-card">
            <div className="hp-trust__job-header">Das bringst du mit</div>
            <div className="hp-trust__job-line">
              <span className="hp-sk hp-sk--md" /><span className="hp-kw">Cloud-Infrastrukturen</span><span className="hp-sk hp-sk--sm" /><span className="hp-kw">AWS</span><span className="hp-sk hp-sk--xs" /><span className="hp-kw">Azure</span>
            </div>
            <div className="hp-trust__job-line">
              <span className="hp-sk hp-sk--sm" /><span className="hp-kw">CI/CD Pipelines</span><span className="hp-sk hp-sk--lg" /><span className="hp-kw">Monitoring</span><span className="hp-sk hp-sk--xs" />
            </div>
            <div className="hp-trust__job-line">
              <span className="hp-sk hp-sk--lg" /><span className="hp-kw">Logging</span><span className="hp-sk hp-sk--sm" /><span className="hp-kw">Alerting</span><span className="hp-sk hp-sk--md" />
            </div>
            <div className="hp-trust__job-line">
              <span className="hp-sk hp-sk--xs" /><span className="hp-kw">Cloud-Architekturen</span><span className="hp-sk hp-sk--lg" />
            </div>
          </div>

          <div className="hp-trust__arrow" aria-hidden="true">→</div>

          <div className="hp-trust__job-card hp-trust__job-card--profile">
            <div className="hp-trust__job-header">Cloud Engineer – Mustermann Solutions</div>
            <div className="hp-trust__job-line">
              <span className="hp-sk hp-sk--sm" /><span className="hp-kw">AWS</span><span className="hp-sk hp-sk--md" /><span className="hp-kw">Azure</span><span className="hp-sk hp-sk--lg" />
            </div>
            <div className="hp-trust__job-line">
              <span className="hp-sk hp-sk--lg" /><span className="hp-kw">Cloud-Infrastrukturen</span><span className="hp-sk hp-sk--xs" /><span className="hp-kw">CI/CD Pipelines</span>
            </div>
            <div className="hp-trust__job-line">
              <span className="hp-sk hp-sk--md" /><span className="hp-kw">Monitoring</span><span className="hp-sk hp-sk--sm" /><span className="hp-kw">Logging</span><span className="hp-sk hp-sk--md" />
            </div>
            <div className="hp-trust__job-line">
              <span className="hp-sk hp-sk--xs" /><span className="hp-kw">Cloud-Architekturen</span><span className="hp-sk hp-sk--sm" /><span className="hp-kw">Alerting</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Ratgeber-Teaser (interne Links für SEO) ───────────────────── */
const RATGEBER = [
  ['/ratgeber/bewerbung-mit-ki-schreiben/', 'Bewerbung mit KI schreiben', 'Erlaubt, sinnvoll oder riskant? Anleitung, Prompts und Grenzen.'],
  ['/ratgeber/lebenslauf-anpassen/', 'Lebenslauf an die Stellenanzeige anpassen', 'Derselbe Werdegang, für jede Stelle richtig gewichtet.'],
  ['/ratgeber/ats-lebenslauf/', 'ATS-konformer Lebenslauf', 'Format, Checkliste und Selbsttest für Bewerbermanagement-Systeme.'],
  ['/ratgeber/anschreiben-aufbau/', 'Anschreiben-Aufbau 2026', 'Alle Bestandteile und ein komplettes Muster.'],
];

function Ratgeber() {
  const ref = useReveal();
  return (
    <section className="hp-ratgeber" id="ratgeber" aria-labelledby="ratgeber-h2">
      <div className="hp-section__inner hp-reveal" ref={ref}>
        <h2 id="ratgeber-h2" className="hp-section__h2">Bewerbungsratgeber</h2>
        <p className="hp-section__lead">Muster, Checklisten und Anleitungen für Lebenslauf, Anschreiben und Bewerbungen mit KI.</p>
        <div className="hp-ratgeber__grid">
          {RATGEBER.map(([href, title, text]) => (
            <a key={href} href={href} className="hp-ratgeber__card">
              <span className="hp-ratgeber__title">{title}</span>
              <span className="hp-ratgeber__text">{text}</span>
              <span className="hp-ratgeber__more">Weiterlesen →</span>
            </a>
          ))}
        </div>
        <a href="/ratgeber/" className="hp-ratgeber__all">Alle Ratgeber-Artikel ansehen →</a>
      </div>
    </section>
  );
}

/* ─── SEO FAQ ───────────────────────────────────────────────────── */
function FAQ() {
  const ref = useReveal();
  const navigate = useNavigate();
  const [openItem, setOpenItem] = useState(0);
  const items = [
    {
      q: 'Was macht CVFlow anders als ein normaler Lebenslauf-Generator?',
      a: 'CVFlow analysiert zuerst die konkrete Stellenanzeige und passt Lebenslauf, Anschreiben und relevante Keywords daran an. Es geht nicht nur um eine schöne Vorlage, sondern um eine Bewerbung, die zur Rolle passt.',
    },
    {
      q: 'Ist der Lebenslauf ATS-optimiert?',
      a: 'Ja. CVFlow achtet auf klare Struktur, relevante Begriffe aus der Stellenanzeige und exportierbare Dokumente, die sich für typische Bewerbermanagement-Systeme eignen.',
    },
    {
      q: 'Kann die KI falsche Erfahrungen erfinden?',
      a: 'Der integrierte Fact-Checker prüft Formulierungen gegen dein Profil und markiert Aussagen, die nicht zu deinen Angaben passen. Du behältst die Kontrolle und bearbeitest alles vor dem Export.',
    },
    {
      q: 'Darf ich meine Bewerbung mit KI schreiben?',
      a: 'Ja. Es gibt kein Verbot, Lebenslauf oder Anschreiben mit KI zu erstellen. Entscheidend ist, dass alle Angaben stimmen. Deshalb gleicht der Fact-Checker jede Aussage mit deinem Profil ab, und du prüfst alles vor dem Export.',
    },
    {
      q: 'Brauche ich ein Abo?',
      a: 'Nein. Du kannst kostenlos starten und Analytics sowie Job-Fit Analyse sofort nutzen. Für unbegrenzte KI-Bewerbungen mit Lebenslauf und Anschreiben wechselst du zu CVFlow Pro.',
    },
  ];

  return (
    <section className="hp-faq" id="faq" aria-labelledby="faq-h2">
      <div className="hp-section__inner hp-faq__inner hp-reveal" ref={ref}>
        <div className="hp-faq__intro">
<h2 id="faq-h2" className="hp-section__h2">
            Kurz geklärt,<br />bevor du deine Bewerbung erstellst.
          </h2>
          <p className="hp-faq__lead">
            Alles Wesentliche zu ATS, KI-Texten, Kontrolle und Kosten, ohne Kleingedrucktes zu verstecken.
          </p>
        </div>
        <div className="hp-faq__grid">
          {items.map((item, index) => {
            const isOpen = openItem === index;
            const panelId = `faq-panel-${index}`;
            return (
              <article className={`hp-faq__item${isOpen ? ' hp-faq__item--open' : ''}`} key={item.q}>
                <h3>
                  <button
                    className="hp-faq__trigger"
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenItem(isOpen ? null : index)}
                  >
                    <span>{item.q}</span>
                    <span className="hp-faq__icon" aria-hidden="true" />
                  </button>
                </h3>
                <div className="hp-faq__panel" id={panelId}>
                  <p>{item.a}</p>
                </div>
              </article>
            );
          })}
          <div className="hp-faq__cta">
            <p>Bereit, deine nächste Bewerbung gezielt auf die Stelle auszurichten?</p>
            <button
              className="hp-btn hp-btn--amber hp-btn--hero"
              onClick={() => starteOnboarding(navigate, 'Jetzt kostenlos testen', 'faq')}
              type="button"
            >
              Jetzt kostenlos testen →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="hp-footer">
      <div className="hp-footer__inner">
        <div className="hp-footer__brand">
          <img src="/logo.png" alt="CVFlow" className="hp-footer__brand-mark" />
        </div>
        <nav className="hp-footer__links" aria-label="Footer-Navigation">
          <a href="/ratgeber/">Ratgeber</a>
          <Link to="/impressum">Impressum</Link>
          <Link to="/agb">AGB</Link>
          <Link to="/datenschutz">Datenschutz</Link>
        </nav>
        <div className="hp-footer__status">
          <span className="hp-footer__status-dot" aria-hidden="true" />
          System Online · Made in Germany
        </div>
      </div>
    </footer>
  );
}

/* ─── Root ──────────────────────────────────────────────────────── */
export default function Homepage() {
  return (
    <div className="hp-root">
      <NavBar />
      <main>
        <Hero />
        <Autopilot />
        <Dashboard />
        <Editor />
        <Preise />
        <Datenschutz />
        <Trust />
        <Ratgeber />
        <FAQ />
      </main>
      <Footer />
    </div>
  );
}
